import { useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PlusIcon, Trash2Icon } from "lucide-react";

import { Button } from "../../../Components/Buttons";
import { FormImage, FormInput, FormSection, FormSelect, FormSwitch } from "../../../Components/Form";
import { ToastMessage } from "../../../Components/Utils";
import BreadCrumb from "../../../Components/BreadCrumb";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { handleError } from "../../../stores/components/handleError";
import { useFetchBrandsAllQuery } from "../../../stores/api/brands.api";
import { useFetchCategoriesAllQuery } from "../../../stores/api/categories.api";
import { useFetchSubCategoriesAllQuery } from "../../../stores/api/subCategories.api";
import {
  useCreateProductMutation,
  useFetchCategoryByBrandIdQuery,
  useFetchSubCategoryByCatIdQuery,
  useUpdateProductMutation,
} from "../../../stores/api/products.api";
import { buildFormData } from "../../../stores/api/helpers/payload.helper";
import { product_image_url } from "../../../Components/ImageUrls";
import Img from "../../User/components/Img";

const slugify = (v) => v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const EMPTY = {
  name: "",
  slug: "",
  brand_id: "",
  category_id: "",
  sub_category_id: "",
  description: "",
  status: true,
  home_featured: false,
  meta_title: "",
  meta_description: "",
  meta_keywords: "",
};

const toOptions = (list) =>
  (Array.isArray(list) ? list : []).map((item) => ({
    value: String(item.id),
    label: item.name,
  }));

const CreateProduct = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const details = location?.state?.product;
  const [brandId, setSelectBrandId] = useState(
    details?.brand_id ? String(details.brand_id) : ""
  );

  const [categoryId, setSelectedCategoryId] = useState(
    details?.category_id ? String(details.category_id) : ""
  );
  const { data: brandCategoriesRaw } = useFetchCategoryByBrandIdQuery(brandId, {
    skip: !brandId,
    refetchOnMountOrArgChange: true,
  })
  const { data: subCategoriesRaw } = useFetchSubCategoryByCatIdQuery(categoryId, {
    skip: !categoryId,
    refetchOnMountOrArgChange: true,
  })
  const { data: brandsRaw } = useFetchBrandsAllQuery();

  const brands = useMemo(() => toOptions(brandsRaw), [brandsRaw]);
  const categories = useMemo(() => toOptions(brandCategoriesRaw), [brandCategoriesRaw]);
  const subCategories = useMemo(() => toOptions(subCategoriesRaw), [subCategoriesRaw]);

  const [createProduct, { isLoading: creating }] = useCreateProductMutation();
  const [updateProduct, { isLoading: updating }] = useUpdateProductMutation();
  const loading = creating || updating;
  const [formData, setFormData] = useState({
    ...EMPTY,
    ...(details
      ? {
        name: details.name || "",
        slug: details.slug || "",
        brand_id: details.brand_id ? String(details.brand_id) : "",
        category_id: details.category_id ? String(details.category_id) : "",
        sub_category_id: details.sub_category_id ? String(details.sub_category_id) : "",
        description: details.description || "",
        image: "",
        image_alt: details.image_alt || "",
        status: details.status ?? true,
        home_featured: details.home_featured ?? true,
        meta_title: details.meta_title || "",
        meta_description: details.meta_description || "",
        meta_keywords: details.meta_keywords || "",
        existing_images: null,
      }
      : {}),
  });
  const [images, setImages] = useState(() => {

    if (!details) return [];

    return (details.images || []).map((img) => ({
      id: img.id,
      file: null,
      preview: `${product_image_url}images/${img.image}`,
      filename: img.image,
      image_alt: img.image_alt || "",
      isPrimary: img.is_primary || false,
      existing: true,
    }));
  });
  const fileInputRef = useRef(null);

  const addImages = (files) => {

    const newImgs = Array.from(files)
      .filter((f) => f.type.startsWith("image/"))
      .map((f) => ({
        file: f,
        preview: URL.createObjectURL(f),
        image_alt: "",
        isPrimary: false,   // ✅ FIX ADDED
        existing: false,
      }));


    setImages((prev) => {

      const combined = [...prev, ...newImgs];

      // ensure only one primary exists
      if (combined.length > 0 && !combined.some(i => i.isPrimary)) {
        combined[0].isPrimary = true;
      }

      return combined;
    });
  };

  const setImageAlt = (idx, val) => {
    setImages((prev) => prev.map((img, i) => (i === idx ? { ...img, image_alt: val } : img)));
  };

  const removeImage = (idx) => {
    setImages((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      if (next.length > 0 && !next.some((i) => i.isPrimary)) next[0].isPrimary = true;
      return next;
    });
  };


  const setField = (name, value) => setFormData((prev) => ({ ...prev, [name]: value }));

  const handleNameChange = (val) => {
    setFormData((prev) => ({
      ...prev,
      name: val,
      slug: prev.slug === slugify(prev.name) || !prev.slug ? slugify(val) : prev.slug,
    }));
  };

  const onSubmit = async (e) => {

    e.preventDefault();

    try {

      const payload = {
        ...formData,

        status: formData.status ? 1 : 0,
        existing_images: null,
        home_featured: formData.home_featured ? 1 : 0,
      };

      if (formData.image instanceof File) {
        payload.image = formData.image;
      }
      const newGalleryImages = images.filter(
        (img) => img.file instanceof File
      );
      if (newGalleryImages.length > 0) {
        payload.images = newGalleryImages.map(
          (img) => img.file
        );
       payload.images_meta = JSON.stringify(
          newGalleryImages.map(img => ({
            image_alt: img.image_alt || "",
            is_primary: !!img.isPrimary,
          }))
        );
      }
      if (details?.images?.length > 0) {
        payload.existing_images = JSON.stringify(
          images
            .filter(img => img.id !== null && img.id !== undefined)
            .map(img => ({
              image: img.filename || "test.png",
              image_alt: img.image_alt || "",
              is_primary: !!img.isPrimary,
              id: img.id
            }))
        );
      }
      const body = buildFormData(payload);
      if (details) {

        await updateProduct({
          id: details.id,
          body
        }).unwrap();

      } else {

        await createProduct(body).unwrap();
      }


      ToastMessage({
        type: "success",
        title: details
          ? "Product updated"
          : "Product created",
        message: details
          ? "Product updated successfully!"
          : "Product created successfully!"
      });


      navigate(ROUTE_PATHS.PRODUCT);

    } catch (err) {

      handleError(err, "Something went wrong.");
    }
  };
  const handleBrandChange = (val) => {
    setSelectBrandId(val);
    setField("brand_id", val ?? "");
    setField("category_id", "");
  };
  const setPrimary = (idx) => {
    setImages((prev) =>
      prev.map((img, i) => ({
        ...img,
        isPrimary: i === idx,
      }))
    );
  };
  const handleCategoryChange = (val) => {
    setSelectedCategoryId(val);
    setField("category_id", val ?? "");
    setField("sub_category_id", "");
  };
  return (
    <div className="p-4">
      <BreadCrumb title={details ? "Edit Product" : "Create Product"} />
      <form onSubmit={onSubmit} className="mx-auto mt-6 dark:bg-slate-900 rounded-lg p-6 space-y-5">
        <FormSection title="Product Details">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormSelect isRequired label="Brand" value={formData.brand_id} onChange={(val) => handleBrandChange(val)} options={brands} />
            <FormSelect isRequired label="Category" value={formData.category_id} onChange={(val) => handleCategoryChange(val)} options={categories} />
            <FormSelect isRequired label="Sub-Category" value={formData.sub_category_id} onChange={(val) => setField("sub_category_id", val ?? "")} options={subCategories} />
          </div><div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput isRequired label="Name" name="name" value={formData.name} onChange={handleNameChange} placeholder="e.g. Nike Air Max" />
            <FormInput isRequired label="Slug" name="slug" value={formData.slug} onChange={(val) => setField("slug", val)} placeholder="auto-generated" />
            <div className="md:col-span-2">
              <FormInput isRequired label="Description" name="description" value={formData.description} onChange={(val) => setFormData((p) => ({ ...p, description: val }))} placeholder="Enter product description" rows={4} />
            </div>
          </div>
        </FormSection>
        <FormSection title="Primary Image">
          <div className="">
            <FormImage label="Image" required initialPreviewUrl={details?.image ? (product_image_url ?? "") + details.image : null} onChange={(file) => setFormData((prev) => ({ ...prev, image: file }))} />
            <FormInput label="Image Alt Text" name="image_alt" value={formData.image_alt} required onChange={(val) => setFormData((p) => ({ ...p, image_alt: val }))} isRequired className='mt-3' placeholder="Describe the image" />
          </div>
        </FormSection>
        <FormSection title="Product Images">
          <input ref={fileInputRef} isRequired type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addImages(e.target.files); e.target.value = ""; }} />

          {images.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-3">
              {images.map((img, idx) => (
                <div key={idx} className={`rounded-xl overflow-hidden border-2 transition-all ${img.isPrimary ? "border-green-500 ring-2 ring-green-300 dark:ring-green-500/50" : "border-gray-200 dark:border-slate-600"}`}>
                  <div className="relative">

                    <Img src={img.preview} alt={img.image_alt} className="w-full h-28 object-cover" />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1.5 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={img.isPrimary || false}
                          onChange={() => setPrimary(idx)}
                          className="w-4 h-4 accent-green-500"
                        />
                        <span className="text-xs text-white">Primary</span>
                      </div>
                      <button type="button" onClick={() => removeImage(idx)} className="p-0.5 rounded bg-red-500/80 text-white hover:bg-red-600 transition">
                        <Trash2Icon size={12} />
                      </button>
                    </div>
                    {img.isPrimary && <span className="absolute top-1.5 left-1.5 text-[9px] font-bold uppercase bg-green-500 text-white px-1.5 py-0.5 rounded-full">Primary</span>}
                  </div>
                  <input
                    type="text"
                    isRequired
                    value={img.image_alt || ""}
                    onChange={(e) => setImageAlt(idx, e.target.value)}
                    placeholder="Image alt..."
                    className="w-full text-xs px-2 py-1.5 border-t border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-green-400"
                  />
                </div>
              ))}
            </div>
          )}

          <button type="button" onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-gray-300 dark:border-slate-600 text-gray-500 dark:text-gray-400 hover:border-green-400 hover:text-green-500 dark:hover:border-green-500 transition text-sm font-medium">
            <PlusIcon size={16} />
            Add Images
          </button>
        </FormSection>

        <FormSection title="SEO">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput isRequired label="Meta Title" name="meta_title" value={formData.meta_title} onChange={(val) => setField("meta_title", val)} placeholder="SEO title" />
            <FormInput label="Meta Keywords" name="meta_keywords" value={formData.meta_keywords} onChange={(val) => setField("meta_keywords", val)} placeholder="keyword1, keyword2" />
            <div className="md:col-span-2">
              <FormInput isRequired label="Meta Description" name="meta_description" value={formData.meta_description} onChange={(val) => setField("meta_description", val)} placeholder="SEO description" rows={3} />
            </div>
          </div>
        </FormSection>

        <FormSection title="Status">
          <div className="flex gap-3 items-center">
            <FormSwitch label="Active" checked={formData.status} onChange={(val) => setFormData((p) => ({ ...p, status: val }))} />
            <FormSwitch label="Home Featured?" checked={formData.home_featured} onChange={(val) => setFormData((p) => ({ ...p, home_featured: val }))} />
          </div>
        </FormSection>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button type="button" onClick={() => navigate(ROUTE_PATHS.PRODUCT)} className="!bg-gray-100 !text-gray-700 hover:!bg-gray-200 dark:!bg-slate-800 dark:!text-gray-200" disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" className="!bg-green-500 !text-white hover:!bg-green-600" loading={loading} disabled={loading} loadingText={details ? "Updating..." : "Creating..."}>
            {details ? "Update Product" : "Create Product"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateProduct;
